import React from 'react';

import EditIcon from '@mui/icons-material/Edit';
import {
  Button,
  FormControl, IconButton, InputLabel, List, MenuItem, Select,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import AddPage from './AddPage';
import DateSelector from './DateSelector';
import {
  classChildSelector,
  dateSelector,
  pageSelector,
} from './selector';
import {
  alreadyRead, refreshHealthRecords, refreshPage,
} from './thunk';

import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { PageJson } from '@/types/domain/childNotebook/entities/pageEntity';
import type { UserType } from '@/types/domain/user/redux';

import Title from '@/components/Title';
import HealthInfo from '@/domain/childNotebook/component/HealthInfo';
import PageInfo from '@/domain/childNotebook/component/PageInfo';
import { uiSlice } from '@/domain/ui/redux';

/**
 * プロパティ
 */
export type Props = {
  /** ユーザー */
  user: UserType,
  /** 子ども */
  child: ChildJson,
  /** 施設リスト */
  facilityList: FacilityJson[],
};

const ChildNotebook: React.FC<Props> = ({
  user,
  child,
  facilityList,
}) => {
  const dispatch = useDispatch();
  const date = useSelector(dateSelector);
  const page = useSelector(pageSelector);
  const classChildList = useSelector(classChildSelector);
  const classChild = classChildList.find(
    (v) => v.data.parentId === child.parentId && v.data.childId === child.childId,
  );
  const hideChildNotebook = classChild?.data.hideChildNotebook || false;
  const hideCondition = classChild?.data.hideCondition || false;
  const healthRecordTypes = classChild?.data.childNotebookContents || [];
  const [facilityId, setFacilityId] = React.useState(facilityList[0]?.data.facilityId || '');
  const [
    addItem,
    setAddItem,
  ] = React.useState<{
    pageApp?: PageJson | undefined;
    page?: PageJson | undefined;
  } | undefined>(undefined);
  React.useEffect(() => {
    if (page === undefined) {
      dispatch(refreshPage());
      dispatch(refreshHealthRecords());
    }
  }, [dispatch, page]);
  React.useEffect(() => {
    if (page?.page !== undefined && page.page.pub.data.alreadyRead === false) {
      // 既読判定
      dispatch(alreadyRead({
        data: page.page,
      }));
    }
  }, [dispatch, date, page, facilityList]);
  const isSunday = (new Date(date)).getDay() === 0;
  if (facilityList.length === 0) {
    return (
      <div>
        <p>有効な施設が見つかりませんでした。</p>
      </div>
    );
  }
  return (
    <div>
      <Title title="連絡帳" />
      <div>
        <DateSelector date={date} />
        {facilityList.length > 1 && (
          <FormControl fullWidth style={{ marginBottom: '10px' }}>
            <InputLabel>施設</InputLabel>
            <Select
              value={facilityId}
              label="施設"
              onChange={(e) => setFacilityId(e.target.value)}
            >
              {facilityList.map((v) => (
                <MenuItem
                  value={v.data.facilityId}
                  key={v.data.facilityId}
                >
                  {v.data.facilityName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {page && (
          <div>
            <h3 style={{ margin: '1.5em 0 0 0' }}>
              健康記録
              <Tooltip title="健康管理画面へ">
                <IconButton size="large" onClick={() => dispatch(uiSlice.actions.setPage('healthRecord'))}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </h3>
            <List>
              <HealthInfo
                date={date}
                healthRecords={page.healthRecords}
                healthRecordTypes={healthRecordTypes}
              />
            </List>
            <h3 style={{ margin: '1.5em 0 0 0' }}>ご家庭入力欄</h3>
            {!addItem && page.pageApp ? (
              <PageInfo
                data={page.pageApp}
                hideCondition={hideCondition}
              />
            ) : (
              <p>未入力</p>
            )}
            <div style={{ margin: '0 1em' }}>
              {hideChildNotebook && (
                <p style={{ color: 'red' }}>※このクラスでは連絡帳の送信は不要です。</p>
              )}
              {page.pageApp?.data.isSent ? (
                <Button
                  fullWidth
                  variant="contained"
                  disabled
                >
                  入力済み
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setAddItem({
                      pageApp: page.pageApp,
                      page: page.page,
                    });
                  }}
                  disabled={hideChildNotebook || isSunday}
                >
                  {page.pageApp ? '編集する' : '新規作成'}
                </Button>
              )}
            </div>
            <hr />
            <h3 style={{ margin: '1.5em 0 0 0' }}>保育所入力欄</h3>
            {page.page ? (
              <PageInfo
                data={page.page}
                hideCondition={hideCondition}
              />
            ) : (
              <p>未入力</p>
            )}
            {addItem && (
              <AddPage
                pageDraft={addItem.pageApp}
                healthRecords={page.healthRecords}
                close={() => setAddItem(undefined)}
                child={child}
                user={user}
                facility={facilityList.find((v) => v.data.facilityId === facilityId)!}
                facilityList={facilityList}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChildNotebook;
